import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { isGravidaPatient } from '@/lib/utils';
import AtalahChart from '@/components/AtalahChart';

const API = process.env.REACT_APP_BACKEND_URL;

const PAL_OPTIONS = [
  { value: 1.4, label: 'Sedentário' },
  { value: 1.55, label: 'Levemente ativo' },
  { value: 1.65, label: 'Moderadamente ativo' },
  { value: 1.8, label: 'Muito ativo' },
  { value: 2.0, label: 'Extremamente ativo' },
];

function NecessidadesTab({ patientId, patient }) {
  const isGravida = isGravidaPatient(patient);
  const [calc, setCalc] = useState({
    weight: '', height: '', age: '', sex: 'F', pal: 1.55,
    goal_weight: '', timeframe_weeks: '12',
  });
  const [results, setResults] = useState(null);
  const [macros, setMacros] = useState({ prot_pct: 25, carb_pct: 50, fat_pct: 25 });
  const [editKcal, setEditKcal] = useState('');
  const [saving, setSaving] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Pré-preenche idade da ficha e peso/altura da timeline unificada (check-ins + InBody/Prozis + avaliações físicas)
  useEffect(() => {
    const bd = patient?.birth_date ? new Date(patient.birth_date) : null;
    const age = bd ? Math.floor((new Date() - bd) / 31557600000) : '';
    setCalc(prev => ({ ...prev, age: age || prev.age, height: patient?.altura || prev.height }));
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/patients/${patientId}/weight-timeline`);
        setCalc(prev => ({
          ...prev,
          weight: data?.latest_peso != null ? data.latest_peso : prev.weight,
          height: data?.latest_altura != null ? data.latest_altura : (patient?.altura || prev.height),
        }));
      } catch {}
    })();
  }, [patient, patientId, refreshKey]);

  const calculate = async () => {
    const w = parseFloat(calc.weight) || 0;
    const h = parseFloat(calc.height) || 0;
    const a = parseFloat(calc.age) || 0;
    const pal = parseFloat(calc.pal) || 1.55;
    const gw = parseFloat(calc.goal_weight) || w;
    const weeks = parseFloat(calc.timeframe_weeks) || 12;
    if (!w || !h || !a) { toast.error('Preenche peso, altura e idade'); return; }
    // ═══ Modelo NIH Body Weight Planner (Hall et al. Lancet 2011) — calculado no backend ═══
    // Fonte única de verdade: motor validado em Python (<100 kcal vs NIH BWP oficial).
    try {
      const { data } = await axios.post(`${API}/api/patients/${patientId}/calculate-macros`, {
        current_weight: w, target_weight: gw, height_cm: h, sex: calc.sex,
        pal, age: a, days: Math.round(weeks * 7),
      });
      setResults({
        bmr: data.rmr,
        tdee: data.tdee,
        goalKcal: data.kcal,
        maintGoal: data.maint_goal_kcal,
        dailyChange: data.kcal - data.tdee,
        achievable: data.achievable,
        warning: data.warning,
        intakeModel: data.intake_model_kcal,
        minDays: data.min_days_suggested,
      });
      setEditKcal(String(data.kcal));
      if (data.achievable === false && data.warning) toast.warning(data.warning);
    } catch (e) {
      toast.error('Erro ao calcular necessidades');
    }
  };

  // Program-specific adjustments (applied after base calculation)
  const pt = patient?.patient_type || 'geral';
  const [gestAdjust, setGestAdjust] = useState(0);
  const [lactAdjust, setLactAdjust] = useState(0);
  const [lactState, setLactState] = useState('exclusiva');
  const [protocolNotes, setProtocolNotes] = useState('');

  // Calculate gestational trimester
  const dumDate = patient?.dum ? new Date(patient.dum) : null;
  const gestWeeks = dumDate ? Math.floor((new Date() - dumDate) / (7 * 24 * 60 * 60 * 1000)) : null;
  const trimester = gestWeeks ? (gestWeeks <= 13 ? 1 : gestWeeks <= 26 ? 2 : 3) : null;

  useEffect(() => {
    if (pt === 'gravida' && trimester) {
      setGestAdjust(trimester === 1 ? 0 : trimester === 2 ? 340 : 452);
    }
    if (pt === 'pos_parto') {
      setLactAdjust(lactState === 'exclusiva' ? 500 : lactState === 'mista' ? 330 : 0);
    }
  }, [pt, trimester, lactState]);

  const updateMacroFromPct = (field, val) => {
    const pct = Math.max(0, Math.min(100, parseInt(val) || 0));
    const newMacros = { ...macros, [field]: pct };
    // Auto-adjust last one to sum 100
    if (field === 'prot_pct') newMacros.fat_pct = Math.max(0, 100 - pct - newMacros.carb_pct);
    if (field === 'carb_pct') newMacros.fat_pct = Math.max(0, 100 - newMacros.prot_pct - pct);
    if (field === 'fat_pct') newMacros.carb_pct = Math.max(0, 100 - newMacros.prot_pct - pct);
    setMacros(newMacros);
  };

  const kcal = (parseFloat(editKcal) || 0) + (pt === 'gravida' ? gestAdjust : pt === 'pos_parto' ? lactAdjust : 0);
  const w = parseFloat(calc.weight) || 70;
  // Protein per program
  let protMultiplier = macros.prot_pct / 100;
  if (pt === 'gravida') protMultiplier = Math.max(protMultiplier, (1.1 * w * 4) / (kcal || 1));
  if (pt === 'pos_parto' && lactState !== 'desmamou') protMultiplier = Math.max(protMultiplier, (1.3 * w * 4) / (kcal || 1));
  const protG = Math.round(kcal * protMultiplier / 4);
  const carbG = Math.round(kcal * (macros.carb_pct / 100) / 4);
  const fatG = Math.round(kcal * (macros.fat_pct / 100) / 9);
  const protGkg = (protG / w).toFixed(1);
  const carbGkg = (carbG / w).toFixed(1);
  const fatGkg = (fatG / w).toFixed(1);
  // Water per program
  let waterBase = Math.round(w * 33);
  if (pt === 'gravida') waterBase += 300;
  if (pt === 'pos_parto' && lactState === 'exclusiva') waterBase += 700;
  if (pt === 'pos_parto' && lactState === 'mista') waterBase += 500;
  const waterMl = waterBase;

  const [existingPlans, setExistingPlans] = useState([]);
  const [showImportDialog, setShowImportDialog] = useState(false);
  useEffect(() => {
    (async () => { try { const { data } = await axios.get(`${API}/api/meal-plans?client_id=${patientId}`); setExistingPlans(data); } catch {} })();
  }, [patientId]);

  const importToPlan = async (planId) => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/patients/${patientId}/goals`, {
        energia_kcal: kcal, proteina_g: protG, hidratos_g: carbG, gordura_g: fatG, fibra_g: 0
      });
      if (planId === 'new') {
        await axios.post(`${API}/api/meal-plans`, {
          client_id: patientId, name: `Plano ${new Date().toLocaleDateString('pt-PT')}`, status: 'rascunho', meals: {},
          nutritional_targets: { energia_kcal: kcal, proteina_g: protG, hidratos_g: carbG, gordura_g: fatG, water_ml: waterMl }
        });
        toast.success('Novo plano criado com os objetivos nutricionais');
      } else if (planId) {
        await axios.put(`${API}/api/meal-plans/${planId}`, {
          nutritional_targets: { energia_kcal: kcal, proteina_g: protG, hidratos_g: carbG, gordura_g: fatG, water_ml: waterMl }
        });
        const planName = existingPlans.find(p => p.id === planId)?.name || '';
        toast.success(`Objetivos importados para "${planName}"`);
      }
      setShowImportDialog(false);
    } catch { toast.error('Erro ao guardar'); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="tab-necessidades">
      {/* Atalah Chart — apenas pacientes com tag grávida */}
      {isGravida && (
        <div className="bg-white border border-brand-border rounded-2xl p-4 mb-4">
          <AtalahChart patientId={patientId} editable onUpdated={() => setRefreshKey(k => k + 1)} />
        </div>
      )}
      {/* Calculator */}
      <div className="bg-white border border-brand-border rounded-2xl p-4 mb-4">
        <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-3">Calculadora de necessidades</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
          <div><Label className="text-[10px] text-brand-muted mb-0.5 block">Peso atual (kg)</Label><Input type="number" step="0.1" value={calc.weight} onChange={e => setCalc({...calc, weight: e.target.value})} className="rounded-lg border-brand-border text-sm h-8" /></div>
          <div><Label className="text-[10px] text-brand-muted mb-0.5 block">Altura (cm)</Label><Input type="number" value={calc.height} onChange={e => setCalc({...calc, height: e.target.value})} className="rounded-lg border-brand-border text-sm h-8" /></div>
          <div><Label className="text-[10px] text-brand-muted mb-0.5 block">Idade</Label><Input type="number" value={calc.age} onChange={e => setCalc({...calc, age: e.target.value})} className="rounded-lg border-brand-border text-sm h-8" /></div>
          <div><Label className="text-[10px] text-brand-muted mb-0.5 block">Sexo</Label>
            <div className="flex gap-1">{['F','M'].map(s => <button key={s} onClick={() => setCalc({...calc, sex: s})} className={`flex-1 py-1.5 rounded-lg text-xs font-medium ${calc.sex === s ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>{s === 'F' ? 'Feminino' : 'Masculino'}</button>)}</div>
          </div>
          <div><Label className="text-[10px] text-brand-muted mb-0.5 block">Atividade física</Label>
            <select value={calc.pal} onChange={e => setCalc({...calc, pal: parseFloat(e.target.value)})} className="w-full border border-brand-border rounded-lg px-2 py-1.5 text-xs bg-white">{PAL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select>
          </div>
        </div>
        {/* Program-specific fields */}
        {pt === 'gravida' && gestWeeks && (
          <div className="bg-brand-mint/5 border border-brand-mint/20 rounded-xl p-3 mb-3">
            <p className="text-[10px] text-brand-green uppercase tracking-wider mb-1">Gravidez</p>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div><span className="text-brand-muted">Semana:</span> <b>{gestWeeks}</b></div>
              <div><span className="text-brand-muted">Trimestre:</span> <b>{trimester}.o</b></div>
              <div><span className="text-brand-muted">Ajuste:</span> <b>+{gestAdjust} kcal/dia</b></div>
            </div>
            <div className="mt-2"><Label className="text-[9px] text-brand-muted">Notas protocolares</Label><Input value={protocolNotes} onChange={e => setProtocolNotes(e.target.value)} placeholder="Ex: diabetes gestacional, ajuste ferro" className="rounded-lg border-brand-border text-xs h-7 mt-0.5" /></div>
          </div>
        )}
        {pt === 'pos_parto' && (
          <div className="bg-brand-green/5 border border-brand-green/20 rounded-xl p-3 mb-3">
            <p className="text-[10px] text-brand-green uppercase tracking-wider mb-1">Pós-parto</p>
            <div className="flex gap-1.5">
              {[{v:'exclusiva',l:'Amamentação exclusiva (+500)'},{v:'mista',l:'Amamentação mista (+330)'},{v:'desmamou',l:'Sem amamentação'}].map(o => (
                <button key={o.v} onClick={() => setLactState(o.v)} className={`flex-1 py-1.5 rounded-lg text-[9px] font-medium ${lactState === o.v ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>{o.l}</button>
              ))}
            </div>
          </div>
        )}
        {pt === 'pre_concecao' && (
          <div className="bg-brand-gold/5 border border-brand-gold/20 rounded-xl p-3 mb-3">
            <p className="text-[10px] text-brand-gold-hover uppercase tracking-wider mb-1">Pré-conceção: micronutrientes-chave</p>
            <p className="text-[9px] text-brand-muted">Ácido fólico 400-800 mcg/dia, Ferro 18 mg/dia, Vitamina D 600 UI/dia, Iodo 150 mcg/dia</p>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div><Label className="text-[10px] text-brand-muted mb-0.5 block">Peso objetivo (kg)</Label><Input type="number" step="0.1" value={calc.goal_weight} onChange={e => setCalc({...calc, goal_weight: e.target.value})} className="rounded-lg border-brand-border text-sm h-8" /></div>
          <div><Label className="text-[10px] text-brand-muted mb-0.5 block">Prazo (semanas)</Label><Input type="number" value={calc.timeframe_weeks} onChange={e => setCalc({...calc, timeframe_weeks: e.target.value})} className="rounded-lg border-brand-border text-sm h-8" /></div>
        </div>
        <button onClick={calculate} className="w-full bg-brand-green text-white rounded-full py-2.5 text-sm font-medium">Calcular</button>
      </div>

      {/* Results */}
      {results && (
        <>
          {results.achievable === false && results.warning && (
            <div className="bg-amber-50 border border-amber-300 rounded-2xl p-3 mb-4" data-testid="macros-unachievable-warning">
              <p className="text-xs font-medium text-amber-800">Objetivo não atingível com ingestão segura</p>
              <p className="text-[11px] text-amber-700 mt-1 leading-relaxed">{results.warning}</p>
              <p className="text-[10px] text-amber-600 mt-1">Valor puro do modelo: {results.intakeModel} kcal/dia (abaixo do limite seguro). Aplicado o valor seguro acima.</p>
            </div>
          )}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <button onClick={() => setEditKcal(String(results.tdee))} className={`bg-white border rounded-2xl p-3 text-center transition-all ${kcal === results.tdee ? 'border-brand-green bg-brand-green/5' : 'border-brand-border hover:border-brand-green/30'}`}>
              <p className="text-xl font-heading font-semibold text-brand-text">{results.tdee}</p>
              <p className="text-[9px] text-brand-muted">kcal manutenção</p>
            </button>
            <button onClick={() => setEditKcal(String(results.goalKcal))} className={`bg-white border rounded-2xl p-3 text-center transition-all ${kcal === results.goalKcal ? 'border-brand-green bg-brand-green/5' : 'border-brand-border hover:border-brand-green/30'}`}>
              <p className="text-xl font-heading font-semibold text-brand-green">{results.goalKcal}</p>
              <p className="text-[9px] text-brand-muted">kcal para objetivo</p>
              <p className="text-[8px] text-brand-placeholder">{results.dailyChange > 0 ? '+' : ''}{results.dailyChange} kcal/dia</p>
            </button>
            <button onClick={() => setEditKcal(String(results.maintGoal))} className={`bg-white border rounded-2xl p-3 text-center transition-all ${kcal === results.maintGoal ? 'border-brand-green bg-brand-green/5' : 'border-brand-border hover:border-brand-green/30'}`}>
              <p className="text-xl font-heading font-semibold text-brand-text">{results.maintGoal}</p>
              <p className="text-[9px] text-brand-muted">kcal manutenção objetivo</p>
            </button>
          </div>

          {/* Editable kcal */}
          <div className="bg-white border border-brand-border rounded-2xl p-4 mb-4">
            <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-2">Valor selecionado (editável)</h3>
            <div className="flex items-center gap-2">
              <Input type="number" value={editKcal} onChange={e => setEditKcal(e.target.value)} className="w-28 rounded-lg border-brand-green text-lg font-heading font-semibold text-brand-green text-center h-10" />
              <span className="text-sm text-brand-muted">kcal/dia</span>
            </div>
          </div>

          {/* Macro distribution */}
          <div className="bg-white border border-brand-border rounded-2xl p-4 mb-4">
            <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-3">Distribuição de macronutrientes</h3>
            <div className="space-y-3">
              {[
                { key: 'prot_pct', label: 'Proteína', g: protG, gkg: protGkg, color: 'text-brand-green' },
                { key: 'carb_pct', label: 'Hidratos', g: carbG, gkg: carbGkg, color: 'text-brand-gold-hover' },
                { key: 'fat_pct', label: 'Gordura', g: fatG, gkg: fatGkg, color: 'text-brand-muted' },
              ].map(m => (
                <div key={m.key} className="flex items-center gap-3">
                  <div className="w-16"><span className={`text-xs font-medium ${m.color}`}>{m.label}</span></div>
                  <div className="flex items-center gap-1">
                    <Input type="number" value={macros[m.key]} onChange={e => updateMacroFromPct(m.key, e.target.value)} className="w-14 rounded-lg border-brand-border text-xs text-center h-7" />
                    <span className="text-[10px] text-brand-muted">%</span>
                  </div>
                  <div className="flex-1 h-2 bg-brand-border rounded-full overflow-hidden"><div className={`h-full rounded-full ${m.key === 'prot_pct' ? 'bg-brand-green' : m.key === 'carb_pct' ? 'bg-brand-gold' : 'bg-brand-muted'}`} style={{ width: `${macros[m.key]}%` }} /></div>
                  <div className="text-right w-24">
                    <span className="text-xs font-heading font-semibold text-brand-text">{m.g}g</span>
                    <span className="text-[9px] text-brand-muted ml-1">({m.gkg} g/kg)</span>
                  </div>
                </div>
              ))}
              <div className="flex justify-between text-[10px] text-brand-muted pt-1 border-t border-brand-border/50">
                <span>Total: {macros.prot_pct + macros.carb_pct + macros.fat_pct}%</span>
                <span>{protG * 4 + carbG * 4 + fatG * 9} kcal calculados</span>
              </div>
            </div>
          </div>

          {/* Water / Hydration */}
          <div className="bg-white border border-brand-border rounded-2xl p-4 mb-4">
            <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-2">Hidratação</h3>
            <div className="flex items-center gap-3">
              <div className="text-center">
                <p className="text-2xl font-heading font-semibold text-blue-500">{(waterMl / 1000).toFixed(1)}L</p>
                <p className="text-[9px] text-brand-muted">por dia</p>
              </div>
              <div className="flex-1 text-[10px] text-brand-muted">
                <p>Base: {Math.round(w * 33)} ml ({w}kg x 33 ml/kg)</p>
                {pt === 'gravida' && <p>Ajuste gravidez: +300 ml</p>}
                {pt === 'pos_parto' && lactState === 'exclusiva' && <p>Ajuste amamentação exclusiva: +700 ml</p>}
                {pt === 'pos_parto' && lactState === 'mista' && <p>Ajuste amamentação mista: +500 ml</p>}
              </div>
            </div>
          </div>

          {/* Import button */}
          <button onClick={() => setShowImportDialog(true)} disabled={saving} className="w-full bg-brand-green text-white rounded-full py-3 font-medium disabled:opacity-50" data-testid="import-to-plan-btn">
            {saving ? 'A guardar...' : 'Importar para plano alimentar'}
          </button>
          {/* Import target dialog */}
          <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
            <DialogContent className="rounded-3xl border-brand-border">
              <DialogHeader><DialogTitle className="font-heading text-brand-text">Importar para qual plano?</DialogTitle></DialogHeader>
              <div className="space-y-2">
                {existingPlans.map(p => (
                  <button key={p.id} onClick={() => importToPlan(p.id)} className="w-full text-left px-3 py-2.5 rounded-xl border border-brand-border hover:border-brand-green/30 hover:bg-brand-green/5 transition-all" data-testid={`import-target-${p.id}`}>
                    <p className="text-sm font-medium text-brand-text">{p.name}</p>
                    <span className={`text-[9px] px-2 py-0.5 rounded-full ${p.status === 'ativo' ? 'bg-brand-mint/20 text-brand-green' : 'bg-brand-bg text-brand-muted'}`}>{p.status}</span>
                  </button>
                ))}
                <button onClick={() => importToPlan('new')} className="w-full text-left px-3 py-2.5 rounded-xl border border-dashed border-brand-green/30 hover:bg-brand-green/5 text-brand-green text-sm font-medium" data-testid="import-target-new">
                  + Criar novo plano
                </button>
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}

export default NecessidadesTab;
